<template>
  <div class="shop-the-look-gallery">
    <div
      class="shop-the-look-gallery__grid"
      :class="{ 'reverse': reverseContent }"
    >
      <div class="shop-the-look-gallery__text">
        <heading
          v-if="headingText"
          class="shop-the-look-gallery__heading"
          :class="headingTextAlign"
          :type="headingType"
          :text="headingText"
        />
        <div
          v-if="content"
          class="shop-the-look-gallery__content"
          :class="contentTextAlign"
          v-html="content"
        />
      </div>
      <div class="shop-the-look-gallery__carousel">
        <media-carousel
          :blocks="blocks"
          :autoplay="!isTouchDevice"
          :autoplay-speed="delay"
        />
      </div>
      <div
        ref="stlGalleryProducts"
        class="shop-the-look-gallery__products"
      >
        <heading
          v-if="productHeadingText"
          class="shop-the-look-gallery__products-heading"
          :class="productHeadingTextAlign"
          :type="productHeadingType"
          :text="productHeadingText"
        />
        <product-grid-simple
          v-if="displayType === 'grid'"
          :product-card-use-packshot="usePackshots"
          :products="products"
          :section-id="liquid.sectionId"
          :tracking-parameters="trackingParameters"
        />

        <product-carousel
          v-else
          :product-card-use-packshot="usePackshots"
          :products="products"
          :section-id="liquid.sectionId"
          :tracking-parameters="trackingParameters"
          :slides-per-view="slidesPerView"
          context="shop-the-look-gallery"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Section: Shop The Look Gallery
 * -----------------------------------------------------------------------------
 * - Displays Media Carousel and specifically selected products in a Carousel (on
 * mobile) or Grid (on tablet an desktop).
 *
 * @param {Number} [delay] - Changing slides delay.
 * @param {String} [headingText] - Heading Text to display.
 * @param {String} [headingType] - Theme Settings for h1-h6 values.
 * @param {String} [headingTextAlign] - Heading Text alignment.
 * @param {String} [content] - Description Text to display.
 * @param {String} [contentTextAlign] - Description Text alignment.
 * @param {String} [productHeadingText] - Products heading Text to display.
 * @param {String} [productHeadingType] - Theme Settings for h1-h6 values.
 * @param {String} [productHeadingTextAlign] - Products heading Text alignment.
 * @param {Boolean} [reverseContent] - Reverse content on desktop. Change the position of the slider and the product grid.
 * @param {Array} blocks - An array of media blocks to display in the carousel.
 * @param {Array} productHandles - Array of product handles
 * @param {String} [liquid.sectionId] - Section's Liquid ID.
 * @param {Object} [trackingParameters] - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String} [trackingParameters.section] - Unique name of the section.
 * @param {Boolean} [usePackshots] - If to use Packshots as Primary Product Card images.
 * @param {Boolean|Object} [slidesPerView] - An object with number of slides to display per `mobile`, `tablet` or `desktop` breakpoints.
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'

import { isVisible, setIntersectionObserver } from '~/helpers/dom'
import { importComponent } from '~/core/mint/helpers'
import breakpoints from '~/helpers/breakpoints'

import Heading from '~components/heading/heading'
import MediaCarousel from '~components/media-carousel/media-carousel'

export default {
  name: 'ShopTheLookGallery',

  components: {
    Heading,
    MediaCarousel,
    ProductCarousel: importComponent('product-carousel'),
    ProductGridSimple: importComponent('product-grid-simple'),
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    delay: {
      type: Number,
      default: 3000,
    },
    headingText: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'h2',
    },
    headingTextAlign: {
      type: String,
      default: '',
    },
    content: {
      type: String,
      default: '',
    },
    contentTextAlign: {
      type: String,
      default: '',
    },
    productHeadingText: {
      type: String,
      default: '',
    },
    productHeadingType: {
      type: String,
      default: 'h3',
    },
    productHeadingTextAlign: {
      type: String,
      default: '',
    },
    reverseContent: {
      type: Boolean,
      default: false,
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    productHandles: {
      type: Array,
      default: () => [],
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    usePackshots: {
      type: Boolean,
      default: false,
    },
    slidesPerView: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      productsReady: false,
      displayType: 'grid',
      isTouchDevice: window.matchMedia('(pointer: coarse)').matches,
      products: [
        {
          key: 0,
        },
        {
          key: 1,
        },
        {
          key: 2,
        },
        {
          key: 3,
        },
      ],
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      viewportWidth: 'width',
    }),
  },
  watch: {

    /**
     * The user has resized their screen.
     */
    viewportWidth() {
      this.setDisplayType()
    },
  },

  created() {
    this.setDisplayType()
  },

  mounted() {
    this.initializeSectionObserver()
  },

  methods: {

    /**
     * Initializes the observer for the 'Shop The Look Gallery' section.
     * - if the section is already visible set the products immediately.
     */
    initializeSectionObserver() {
      const targetElement = this.$refs.stlGalleryProducts

      if (isVisible(targetElement)) {
        this.setProducts()
        return
      }

      setIntersectionObserver({
        callback: this.handleSectionIntersection,
        root: targetElement.parentNode,
        rootMargin: '20px 0px',
        element: targetElement,
        threshold: 0.1,
      })
    },

    /**
     * Handle event when a section is scrolled into view.
     * @param {Array} entries - Array of Intersection Observer entries.
     * @param {Object} observer - Intersection Observer.
     */
    handleSectionIntersection(entries, observer) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return
        }

        /**
         * All of the products have loaded.
         */
        if (this.productsReady) {
          observer.disconnect()
          return
        }

        this.setProducts()
      })
    },

    /**
     * Set the products that will be displayed in the component.
     */
    async setProducts() {
      try {

        /**
         * If in the Theme Editor, don't query and render products.
         * - GQL can also be unreliable within the Theme Editor, so prevent
         *   queries in case no products load and the section hides itself.
         */
        // if (mint.request.designMode) {
        // return
        // }

        const queries = this.productHandles.map((handle) => {
          return mint.GetProduct({ handle })
        })

        const response = await Promise.allSettled(queries)

        /**
         * If the query fails, it's likely that the product isn't
         * available on the Storefront API channel.
         */
        this.products = response
          .map((promise) => {
            if (
              promise.status !== 'fulfilled' ||
              !promise.value?.handle
            ) {
              return false
            }

            return promise.value
          })
          .filter(Boolean)

        this.productsReady = true
      } catch (error) {
        mint.log('error', 'Error fetching products', error)
      }
    },

    /**
     * Determines how to display the list of "Shop The Look" products
     *
     * This currently works as follows:
     * - on mobile devices: `carousel` view
     * - on tablets: `grid` view
     * - on the desktop: also a `grid` view
     */
    setDisplayType() {
      this.displayType = window.matchMedia(
        `(min-width: ${breakpoints.medium})`,
      ).matches
        ? 'grid'
        : 'carousel'
    },
  },
}
</script>

<style src="./shop-the-look-gallery.scss" lang="scss"></style>
